import { put, takeEvery, call, takeLatest } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchMCs,
  FetchMCsCounts,
  FetchMC,
  FetchMCCustomers,
  FetchMCsWithPendingRequests,
  UpdateMCExternalId,
  ActivateMC,
  DeactivateMC,
  AcceptCustomerInvite,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from '../api/callApi'
import { Pagination } from 'src/interfaces/table'
import { MCStatus } from 'src/constants/mc/enums'
import { MC, MCCounts, MCCustomer, MCDetails, MCWithPendingRequests } from 'src/interfaces/mc'

export interface FetchMCsPayload {
  tags?: string[]
  search?: string
  status?: MCStatus
  limit: number
  offset: number
  sort?: string
}

function* onFetchMCs(
  action: AsyncAction<FetchMCsPayload, { data: Array<MC>; pagination: Pagination }>,
) {
  const { status, search, tags, limit, offset, sort } = action.payload

  try {
    const { payload }: { payload: { items: Array<MC>; pagination: Pagination } } = yield call(
      callSecureApi,
      Endpoints.FetchMCs,
      {
        apiService: ApiService.Paraworks,
        query: {
          status,
          search,
          tags,
          limit,
          offset,
          sort,
        },
      },
    )

    yield put(
      FetchMCs.success({
        data: payload.items,
        pagination: payload.pagination,
      }),
    )

    if (action.next) {
      action.next(null, {
        data: payload.items,
        pagination: {
          limit: 0,
          offset: 0,
          total: 0,
        },
      })
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch master contractors'

    yield put(FetchMCs.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchMCPayload {
  masterContractorId: string
}

function* onFetchMC(action: AsyncAction<FetchMCPayload, MCDetails>) {
  const { masterContractorId } = action.payload

  try {
    const { payload }: { payload: { masterContractor: MCDetails } } = yield call(
      callSecureApi,
      Endpoints.FetchMC,
      {
        apiService: ApiService.Paraworks,
        query: {
          masterContractorId,
        },
      },
    )

    yield put(FetchMC.success(payload.masterContractor))

    if (action.next) {
      action.next(null, payload.masterContractor)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch master contractor'

    yield put(FetchMC.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchMCsCounts(action: AsyncAction<void, MCCounts>) {
  try {
    const { payload }: { payload: MCCounts } = yield call(callSecureApi, Endpoints.FetchMCsCounts, {
      apiService: ApiService.Paraworks,
    })

    yield put(FetchMCsCounts.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch onboardings counts'

    yield put(FetchMCsCounts.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchMCCustomers(action: AsyncAction<void, { data: Array<MCCustomer> }>) {
  try {
    const { payload }: { payload: { data: Array<MCCustomer> } } = yield call(
      callSecureApi,
      Endpoints.FetchMCCustomers,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchMCCustomers.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch customers'

    yield put(FetchMCCustomers.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchMCsWithPendingRequests(action: AsyncAction<void, MCWithPendingRequests[]>) {
  try {
    const { payload }: { payload: { items: MCWithPendingRequests[] } } = yield call(
      callSecureApi,
      Endpoints.FetchMCsWithPendingRequests,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchMCsWithPendingRequests.success(payload.items))

    if (action.next) {
      action.next(null, payload.items)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch master contractors'

    yield put(FetchMCsWithPendingRequests.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateMCExternalIdRequest {
  masterContractorId: string
  externalId: string
}

function* onUpdateMCExternalId(action: AsyncAction<UpdateMCExternalIdRequest, { id: string }>) {
  const { masterContractorId, externalId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.UpdateMCExternalId, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        masterContractorId,
        externalId,
      },
      returnResponse: true,
    })

    yield put(UpdateMCExternalId.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update master contractor ID'

    yield put(UpdateMCExternalId.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface ActivateMCRequest {
  masterContractorId: string
}

function* onActivateMC(action: AsyncAction<ActivateMCRequest, { id: string }>) {
  const { masterContractorId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.ActivateMC, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        masterContractorId,
      },
      returnResponse: true,
    })

    yield put(ActivateMC.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to activate master contractor'

    yield put(ActivateMC.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DeactivateMCRequest {
  masterContractorId: string
}

function* onDeactivateMC(action: AsyncAction<DeactivateMCRequest, { id: string }>) {
  const { masterContractorId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.DeactivateMC, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        masterContractorId,
      },
      returnResponse: true,
    })

    yield put(DeactivateMC.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to deactivate master contractor'

    yield put(DeactivateMC.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface AcceptCustomerInviteRequest {
  customerId: string
}

function* onAcceptCustomerInvite(action: AsyncAction<AcceptCustomerInviteRequest>) {
  const { customerId } = action.payload

  try {
    yield call(callSecureApi, Endpoints.AcceptCustomerInvite, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        customerId,
      },
      returnResponse: true,
    })

    yield put(AcceptCustomerInvite.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to accept customer invite'

    yield put(AcceptCustomerInvite.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* mcSharedSaga() {
  yield takeEvery(FetchMCs.type.REQUEST, onFetchMCs)
  yield takeEvery(FetchMCsCounts.type.REQUEST, onFetchMCsCounts)
  yield takeLatest(FetchMC.type.REQUEST, onFetchMC)
  yield takeEvery(FetchMCCustomers.type.REQUEST, onFetchMCCustomers)
  yield takeEvery(FetchMCsWithPendingRequests.type.REQUEST, onFetchMCsWithPendingRequests)
  yield takeEvery(UpdateMCExternalId.type.REQUEST, onUpdateMCExternalId)
  yield takeEvery(ActivateMC.type.REQUEST, onActivateMC)
  yield takeEvery(DeactivateMC.type.REQUEST, onDeactivateMC)
  yield takeEvery(AcceptCustomerInvite.type.REQUEST, onAcceptCustomerInvite)
}
